import React from "react";
import { siteData } from "../data/mock";
import { ArrowDown, MapPin, Calendar } from "lucide-react";

export const HeroVariant = ({
  bgImage = "/white-gradient-1.avif",
  darkOverlay = false,
  accentColor = "#FE3A80",
}) => {
  const scrollToAbout = () => {
    const el = document.querySelector("#about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const textColor = darkOverlay ? "#ffffff" : "#1f2937";
  const mutedColor = darkOverlay ? "#d1d5db" : "#6b7280";
  const outlineColor = darkOverlay ? "#ffffff" : accentColor;

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden" style={{ background: "#ffffff" }}>
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src={bgImage}
          alt="Background"
          className="w-full h-full object-cover"
          loading="eager"
        />
        {darkOverlay && (
          <>
            <div
              className="absolute inset-0"
              style={{
                background: "linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.7) 100%)",
              }}
            />
            <div
              className="absolute bottom-0 left-0 right-0 h-40"
              style={{ background: "linear-gradient(to top, #ffffff, transparent)" }}
            />
          </>
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[87.5rem] mx-auto px-10 pb-20 pt-40">
        {/* Event Label */}
        <div className="mb-8">
          <span
            className="inline-block text-xs font-medium uppercase tracking-[0.2em] mb-4"
            style={{ fontFamily: "'Inter', Arial, sans-serif", color: mutedColor }}
          >
            A Walker Ottawa Event
          </span>
        </div>

        {/* Main Headline */}
        <h1
          className="uppercase leading-[0.76] mb-8"
          style={{
            fontFamily: "'Montserrat', Arial, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(3rem, 3.5rem + 2.5vw, 6rem)",
            maxWidth: "15ch",
            color: accentColor,
          }}
        >
          Built to Lead
        </h1>

        {/* Subtitle */}
        <p
          className="text-lg md:text-xl font-medium leading-relaxed max-w-2xl mb-10"
          style={{ fontFamily: "'Inter', Arial, sans-serif", color: textColor }}
        >
          {siteData.tagline}
        </p>

        {/* Event Meta */}
        <div className="flex flex-wrap items-center gap-6 mb-12">
          <div className="flex items-center gap-2" style={{ color: mutedColor }}>
            <Calendar size={16} style={{ color: accentColor }} />
            <span className="text-sm font-medium" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
              {siteData.date} &middot; {siteData.time}
            </span>
          </div>
          <a
            href="https://www.google.com/maps?gs_lcrp=EgZjaHJvbWUqDQgBEC4YrwEYxwEYgAQyCggAEEUYFhgeGDkyDQgBEC4YrwEYxwEYgAQyBwgCEAAYgAQyBwgDEAAYgAQyCAgEEAAYFhgeMggIBRAAGBYYHjIICAYQABgWGB4yCAgHEAAYFhgeMggICBAAGBYYHtIBCDM1OTFqMGo3qAIAsAIA&um=1&ie=UTF-8&fb=1&gl=ca&sa=X&geocode=KQEAAASqBc5MMe-h3NlgfxTY&daddr=245+Centrum+Blvd,+Orl%C3%A9ans,+ON+K1E+0A1"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            style={{ color: mutedColor }}
          >
            <MapPin size={16} style={{ color: accentColor }} />
            <span className="text-sm font-medium underline" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
              {siteData.location}
            </span>
          </a>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-wrap gap-4 mb-16">
          <a
            href="https://shenkmanarts.ca/en/built-to-lead"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center border-none rounded-[10rem] px-8 py-4 text-sm font-semibold uppercase cursor-pointer transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98] no-underline"
            style={{ fontFamily: "'Inter', Arial, sans-serif", letterSpacing: "-0.04em", backgroundColor: accentColor, color: "#ffffff" }}
          >
            Get Tickets
          </a>
          <a
            href="#speakers"
            className="inline-flex items-center justify-center bg-transparent rounded-[10rem] px-8 py-4 text-sm font-semibold uppercase cursor-pointer transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] no-underline"
            style={{
              fontFamily: "'Inter', Arial, sans-serif",
              letterSpacing: "-0.04em",
              color: outlineColor,
              border: `1px solid ${outlineColor}`,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = accentColor; e.currentTarget.style.color = "#ffffff"; e.currentTarget.style.borderColor = accentColor; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = outlineColor; e.currentTarget.style.borderColor = outlineColor; }}
            onClick={(e) => { e.preventDefault(); document.querySelector("#speakers")?.scrollIntoView({ behavior: "smooth" }); }}
          >
            Meet the Speakers
          </a>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={scrollToAbout}
          className="flex items-center gap-2 bg-transparent border-none cursor-pointer group transition-colors duration-300"
          style={{ color: mutedColor }}
          onMouseEnter={(e) => { e.currentTarget.style.color = accentColor; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = mutedColor; }}
        >
          <ArrowDown size={16} className="animate-bounce" />
          <span className="text-xs uppercase tracking-[0.15em] font-medium" style={{ fontFamily: "'Inter', Arial, sans-serif" }}>
            Discover More
          </span>
        </button>
      </div>
    </section>
  );
};
